import React, { useEffect, useRef, useState } from "react";
import { getFirestore, collection, getDocs, doc, setDoc, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../firebase";
import { FiEdit2, FiTrash2, FiSave, FiX, FiSend, FiImage, FiAlertTriangle } from "react-icons/fi";
import useUnsavedChanges from "../hooks/useUnsavedChanges";

// ── helpers ──────────────────────────────────────────────────────
function formatDate(date) {
    if (!date) return "No date";
    return new Intl.DateTimeFormat("en-US", {
        day: "numeric", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit", hour12: true,
    }).format(date);
}

// Resizes to ≤1600px on the long edge and re-encodes as JPEG, stepping the
// quality down until the blob is ≤500KB (the mobile app loads imageUrl as-is
// with no server-side resizing).
function compressImage(file, { maxDimension = 1600, maxBytes = 500 * 1024 } = {}) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(objectUrl);
            let { width, height } = img;
            if (width > maxDimension || height > maxDimension) {
                const scale = maxDimension / Math.max(width, height);
                width = Math.round(width * scale);
                height = Math.round(height * scale);
            }
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            canvas.getContext("2d").drawImage(img, 0, 0, width, height);

            const tryQuality = (quality) => {
                canvas.toBlob((blob) => {
                    if (!blob) { reject(new Error("Image compression failed")); return; }
                    if (blob.size <= maxBytes || quality <= 0.4) resolve(blob);
                    else tryQuality(quality - 0.1);
                }, "image/jpeg", quality);
            };
            tryQuality(0.85);
        };
        img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error("Could not load image")); };
        img.src = objectUrl;
    });
}

async function uploadDevMessageImage(postId, file) {
    const blob = await compressImage(file);
    const imgRef = storageRef(storage, `devmessages/${postId}/image.jpg`);
    await uploadBytes(imgRef, blob, { contentType: "image/jpeg" });
    return getDownloadURL(imgRef);
}

async function deleteDevMessageImage(postId) {
    try {
        await deleteObject(storageRef(storage, `devmessages/${postId}/image.jpg`));
    } catch {
        // nothing uploaded for this post — fine
    }
}

function IconBtn({ icon, color, border, onClick, title, disabled }) {
    const ref = useRef(null);
    return (
        <button
            ref={ref}
            title={title}
            onClick={onClick}
            disabled={disabled}
            onMouseDown={() => { if (ref.current && !disabled) { ref.current.style.transform = "translateY(2px)"; ref.current.style.borderBottomWidth = "2px"; } }}
            onMouseUp={() => { if (ref.current) { ref.current.style.transform = ""; ref.current.style.borderBottomWidth = "3px"; } }}
            onMouseLeave={() => { if (ref.current) { ref.current.style.transform = ""; ref.current.style.borderBottomWidth = "3px"; } }}
            style={{
                width: 34, height: 34, borderRadius: 10,
                border: `1.5px solid ${border}`, borderBottomWidth: 3,
                background: disabled ? "#eee" : color, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: disabled ? "not-allowed" : "pointer", flexShrink: 0,
                transition: "transform 80ms ease, border-bottom-width 80ms ease",
                opacity: disabled ? 0.5 : 1,
            }}
        >
            {icon}
        </button>
    );
}

// ── Styled input/textarea ────────────────────────────────────────
const fieldStyle = {
    fontFamily: "'Quicksand', sans-serif", fontSize: 14, fontWeight: 600,
    padding: "10px 14px", borderRadius: 12,
    border: "1.5px solid #E0E0E0", background: "#f7f9fb",
    width: "100%", outline: "none", color: "#000",
    boxSizing: "border-box", resize: "vertical",
    transition: "border-color 0.15s",
};

// ── Image picker ─────────────────────────────────────────────────
function ImagePicker({ previewUrl, onSelect, onRemove }) {
    const inputRef = useRef(null);
    return (
        <div>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) onSelect(file);
                    e.target.value = "";
                }}
            />
            {previewUrl ? (
                <div style={{ position: "relative", width: 180 }}>
                    <img
                        src={previewUrl}
                        alt=""
                        style={{
                            width: "100%", aspectRatio: "16/9", objectFit: "cover",
                            display: "block", borderRadius: 12, border: "1.5px solid #E0E0E0",
                        }}
                    />
                    <button
                        type="button"
                        onClick={onRemove}
                        title="Remove image"
                        style={{
                            position: "absolute", top: 6, right: 6,
                            width: 24, height: 24, borderRadius: "50%",
                            border: "none", background: "rgba(0,0,0,0.55)", color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: "pointer",
                        }}
                    >
                        <FiX size={13} />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 13,
                        padding: "8px 14px", borderRadius: 10, cursor: "pointer",
                        border: "1.5px solid #E0E0E0", borderBottomWidth: 3,
                        background: "#f7f9fb", color: "#606060",
                    }}
                >
                    <FiImage size={14} /> Add image
                </button>
            )}
        </div>
    );
}

// ── Post card ────────────────────────────────────────────────────
function PostCard({ post, isEditing, editingPost, editingImagePreview, onEdit, onUpdate, onDelete, onCancel, onEditChange, onEditImageSelect, onEditImageRemove, saving }) {
    const isRecent = post.posted && (Date.now() - post.posted.getTime()) < 1000 * 60 * 60 * 24 * 2;

    return (
        <div style={{
            background: "#fff", borderRadius: 18,
            border: `1.5px solid ${isEditing ? "#2a9d8f" : "#E0E0E0"}`,
            borderBottom: `3px solid ${isEditing ? "#2a9d8f" : "#E0E0E0"}`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            padding: "20px 24px",
            display: "flex", flexDirection: "column", gap: 12,
            transition: "border-color 0.2s ease",
        }}>
            {isEditing ? (
                /* ── Edit mode ── */
                <>
                    <input
                        type="text"
                        value={editingPost.title}
                        onChange={e => onEditChange({ ...editingPost, title: e.target.value })}
                        placeholder="Title"
                        autoFocus
                        style={{ ...fieldStyle }}
                        onFocus={e => { e.target.style.borderColor = "#2a9d8f"; }}
                        onBlur={e => { e.target.style.borderColor = "#E0E0E0"; }}
                    />
                    <textarea
                        value={editingPost.description}
                        onChange={e => onEditChange({ ...editingPost, description: e.target.value })}
                        placeholder="Description"
                        rows={3}
                        style={{ ...fieldStyle }}
                        onFocus={e => { e.target.style.borderColor = "#2a9d8f"; }}
                        onBlur={e => { e.target.style.borderColor = "#E0E0E0"; }}
                    />
                    <ImagePicker
                        previewUrl={editingImagePreview || editingPost.imageUrl || null}
                        onSelect={onEditImageSelect}
                        onRemove={onEditImageRemove}
                    />
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button
                            onClick={() => onCancel()}
                            disabled={saving}
                            style={{
                                fontFamily: "'Quicksand', sans-serif", fontWeight: 700, fontSize: 13,
                                padding: "8px 16px", borderRadius: 10, cursor: "pointer",
                                border: "1.5px solid #E0E0E0", borderBottomWidth: 3,
                                background: "#fff", color: "#606060",
                                display: "flex", alignItems: "center", gap: 6,
                            }}
                        >
                            <FiX size={13} /> Cancel
                        </button>
                        <button
                            onClick={() => onUpdate(post.id)}
                            disabled={saving || !editingPost.title.trim() || !editingPost.description.trim()}
                            className="primary-button"
                            style={{
                                backgroundColor: "#2a9d8f", color: "#fff", borderColor: "#258D80",
                                fontSize: 13, padding: "8px 18px",
                                display: "inline-flex", alignItems: "center", gap: 6,
                                opacity: (!editingPost.title.trim() || !editingPost.description.trim()) ? 0.5 : 1,
                            }}
                        >
                            <FiSave size={13} /> {saving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </>
            ) : (
                /* ── View mode ── */
                <>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                        <div style={{ fontWeight: 800, fontSize: 16, lineHeight: 1.3, flex: 1 }}>
                            {post.title}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                            {isRecent && (
                                <span style={{
                                    background: "#2a9d8f", color: "#fff",
                                    borderRadius: 999, padding: "2px 9px",
                                    fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                }}>
                                    New
                                </span>
                            )}
                            <span style={{
                                fontSize: 12, fontWeight: 600, color: "#606060",
                                background: "#f7f9fb", border: "1px solid #E0E0E0",
                                borderRadius: 999, padding: "3px 10px",
                                whiteSpace: "nowrap",
                            }}>
                                {formatDate(post.posted)}
                            </span>
                        </div>
                    </div>
                    {post.imageUrl && (
                        <img
                            src={post.imageUrl}
                            alt=""
                            style={{
                                width: "100%", maxWidth: 360, aspectRatio: "16/9", objectFit: "cover",
                                borderRadius: 12, border: "1px solid #E0E0E0",
                            }}
                        />
                    )}
                    <p style={{ margin: 0, fontSize: 14, color: "#333", lineHeight: 1.65, fontWeight: 500 }}>
                        {post.description}
                    </p>
                    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #f3f3f3", paddingTop: 10 }}>
                        <IconBtn
                            icon={<FiEdit2 size={14} />}
                            color="#e9c46a" border="#d4b15f"
                            title="Edit"
                            onClick={() => onEdit(post)}
                        />
                        <IconBtn
                            icon={<FiTrash2 size={14} />}
                            color="#e76f51" border="#cf6348"
                            title="Delete"
                            onClick={() => onDelete(post.id)}
                        />
                    </div>
                </>
            )}
        </div>
    );
}

// ── Announcements page ───────────────────────────────────────────
function Announcements() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editingPost, setEditingPost] = useState({ title: "", description: "", imageUrl: "" });
    const [editingImageFile, setEditingImageFile] = useState(null);
    const [editingImagePreview, setEditingImagePreview] = useState(null);
    const [newPost, setNewPost] = useState({ title: "", description: "" });
    const [newImageFile, setNewImageFile] = useState(null);
    const [newImagePreview, setNewImagePreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteError, setDeleteError] = useState("");
    const db = getFirestore();

    const hasUnsaved =
        !!newPost.title.trim() ||
        !!newPost.description.trim() ||
        !!newImageFile ||
        editingId !== null ||
        !!editingImageFile;

    // Confirm before leaving (in-app navigation + tab close) with unsaved work.
    useUnsavedChanges(hasUnsaved && !saving);

    useEffect(() => {
        const fetchPosts = async () => {
            const snap = await getDocs(collection(db, "devmessages"));
            const data = snap.docs.map(d => {
                const pd = d.data();
                return {
                    id: d.id,
                    title: pd.title || "",
                    description: pd.description || "",
                    imageUrl: pd.imageUrl || "",
                    posted: pd.posted
                        ? new Date(pd.posted.seconds ? pd.posted.seconds * 1000 : pd.posted)
                        : null,
                };
            });
            data.sort((a, b) => (b.posted?.getTime?.() || 0) - (a.posted?.getTime?.() || 0));
            setPosts(data);
            setLoading(false);
        };
        fetchPosts();
    }, [db]);

    const handleNewImageSelect = (file) => {
        if (newImagePreview) URL.revokeObjectURL(newImagePreview);
        setNewImageFile(file);
        setNewImagePreview(URL.createObjectURL(file));
    };

    const handleNewImageRemove = () => {
        if (newImagePreview) URL.revokeObjectURL(newImagePreview);
        setNewImageFile(null);
        setNewImagePreview(null);
    };

    const handleEditImageSelect = (file) => {
        if (editingImagePreview) URL.revokeObjectURL(editingImagePreview);
        setEditingImageFile(file);
        setEditingImagePreview(URL.createObjectURL(file));
    };

    const handleEditImageRemove = () => {
        if (editingImagePreview) URL.revokeObjectURL(editingImagePreview);
        setEditingImageFile(null);
        setEditingImagePreview(null);
        setEditingPost(prev => ({ ...prev, imageUrl: "" }));
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newPost.title.trim() || !newPost.description.trim()) return;
        setSaving(true);
        try {
            const ref = doc(collection(db, "devmessages"));
            let imageUrl = "";
            if (newImageFile) {
                imageUrl = await uploadDevMessageImage(ref.id, newImageFile);
            }
            const postData = {
                title: newPost.title.trim(),
                description: newPost.description.trim(),
                posted: Timestamp.now(),
                ...(imageUrl ? { imageUrl } : {}),
            };
            await setDoc(ref, postData);
            setPosts(prev => [{ id: ref.id, ...postData, posted: new Date() }, ...prev]);
            setNewPost({ title: "", description: "" });
            handleNewImageRemove();
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async (id) => {
        if (!editingPost.title.trim() || !editingPost.description.trim()) return;
        setSaving(true);
        try {
            let imageUrl = editingPost.imageUrl || "";
            if (editingImageFile) {
                imageUrl = await uploadDevMessageImage(id, editingImageFile);
            } else if (!imageUrl) {
                await deleteDevMessageImage(id);
            }
            await updateDoc(doc(db, "devmessages", id), {
                title: editingPost.title.trim(),
                description: editingPost.description.trim(),
                imageUrl,
            });
            setPosts(prev => prev.map(p => p.id === id
                ? { ...p, title: editingPost.title.trim(), description: editingPost.description.trim(), imageUrl }
                : p
            ));
            setEditingId(null);
            setEditingPost({ title: "", description: "", imageUrl: "" });
            setEditingImageFile(null);
            setEditingImagePreview(null);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this announcement?")) return;
        setDeleteError("");
        try {
            await deleteDoc(doc(db, "devmessages", id));
            await deleteDevMessageImage(id);
            setPosts(prev => prev.filter(p => p.id !== id));
        } catch (err) {
            setDeleteError(err?.code === "permission-denied"
                ? "Permission denied. Your CMS admin account needs delete access to announcements."
                : `Delete failed: ${err.message}`);
        }
    };

    const handleEdit = (post) => {
        setEditingId(post.id);
        setEditingPost({ title: post.title, description: post.description, imageUrl: post.imageUrl || "" });
        setEditingImageFile(null);
        setEditingImagePreview(null);
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditingPost({ title: "", description: "", imageUrl: "" });
        setEditingImageFile(null);
        setEditingImagePreview(null);
    };

    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", gap: 12, color: "#606060", fontWeight: 600, fontFamily: "'Quicksand', sans-serif" }}>
                <div style={{ width: 20, height: 20, border: "2.5px solid #E0E0E0", borderTop: "2.5px solid #2a9d8f", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                Loading announcements…
            </div>
        );
    }

    return (
        <div style={{ fontFamily: "'Quicksand', sans-serif", maxWidth: 760 }}>

            {/* ── Page header ── */}
            <div style={{ marginBottom: 28 }}>
                <h1 style={{ margin: 0 }}>Announcements</h1>
                <div style={{ color: "#606060", fontWeight: 600, fontSize: 14, marginTop: 4 }}>
                    {posts.length} post{posts.length !== 1 ? "s" : ""} · sent to all Kubaya users
                </div>
            </div>

            {deleteError && (
                <div style={{ color: "#e76f51", fontSize: 13, fontWeight: 600, marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
                    <FiAlertTriangle size={14} /> {deleteError}
                </div>
            )}

            {/* ── Compose card ── */}
            <form onSubmit={handleCreate}>
                <div style={{
                    background: "#fff", borderRadius: 20,
                    border: "1.5px solid #E0E0E0", borderBottom: "3px solid #E0E0E0",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    padding: "22px 24px", marginBottom: 24,
                }}>
                    <div style={{
                        fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                        letterSpacing: "0.08em", color: "#2a9d8f", marginBottom: 14,
                    }}>
                        New Announcement
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        <input
                            type="text"
                            placeholder="Title"
                            value={newPost.title}
                            onChange={e => setNewPost({ ...newPost, title: e.target.value })}
                            required
                            style={{ ...fieldStyle }}
                            onFocus={e => { e.target.style.borderColor = "#2a9d8f"; }}
                            onBlur={e => { e.target.style.borderColor = "#E0E0E0"; }}
                        />
                        <textarea
                            placeholder="Write your announcement…"
                            value={newPost.description}
                            onChange={e => setNewPost({ ...newPost, description: e.target.value })}
                            rows={3}
                            required
                            style={{ ...fieldStyle }}
                            onFocus={e => { e.target.style.borderColor = "#2a9d8f"; }}
                            onBlur={e => { e.target.style.borderColor = "#E0E0E0"; }}
                        />
                        <ImagePicker
                            previewUrl={newImagePreview}
                            onSelect={handleNewImageSelect}
                            onRemove={handleNewImageRemove}
                        />
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button
                                type="submit"
                                className="primary-button"
                                disabled={saving || !newPost.title.trim() || !newPost.description.trim()}
                                style={{
                                    backgroundColor: "#2a9d8f", color: "#fff", borderColor: "#258D80",
                                    fontSize: 14, padding: "10px 24px",
                                    display: "inline-flex", alignItems: "center", gap: 8,
                                    opacity: (!newPost.title.trim() || !newPost.description.trim()) ? 0.5 : 1,
                                    cursor: (!newPost.title.trim() || !newPost.description.trim()) ? "not-allowed" : "pointer",
                                }}
                            >
                                <FiSend size={14} /> {saving ? "Publishing…" : "Publish"}
                            </button>
                        </div>
                    </div>
                </div>
            </form>

            {/* ── Posts feed ── */}
            {posts.length === 0 ? (
                <div style={{
                    background: "#fff", borderRadius: 20, border: "1.5px dashed #E0E0E0",
                    padding: "48px 32px", textAlign: "center",
                    color: "#aaa", fontWeight: 600, fontSize: 15,
                }}>
                    No announcements yet. Publish one above!
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                    {posts.map(post => (
                        <PostCard
                            key={post.id}
                            post={post}
                            isEditing={editingId === post.id}
                            editingPost={editingPost}
                            editingImagePreview={editingImagePreview}
                            saving={saving}
                            onEdit={handleEdit}
                            onUpdate={handleUpdate}
                            onDelete={handleDelete}
                            onCancel={handleCancel}
                            onEditChange={setEditingPost}
                            onEditImageSelect={handleEditImageSelect}
                            onEditImageRemove={handleEditImageRemove}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default Announcements;
